import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, ArrowLeft, Tag } from 'lucide-react';
import SEO from '../components/SEO';
import { prefetchRoute } from '../lib/route_prefetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_MAP = {
  '': 'הכל',
  'retainer_trap': 'מלכודת הריטיינר',
  'affiliate_solution': 'שיווק שותפים',
  'authority': 'סמכות והוכחות',
};

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeCategory) params.append('category', activeCategory);
        const res = await fetch(`${API_URL}/api/blog/posts?${params}`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (e) {
        console.error('Error fetching posts:', e);
      }
      setLoading(false);
    };
    fetchPosts();
    // Preload the article-page chunk in idle, so clicking a card feels instant
    prefetchRoute.primeAfter(['/blog/*', '/'], 2000);
  }, [activeCategory]);

  const handleCategoryClick = (cat) => {
    if (cat) setSearchParams({ category: cat });
    else setSearchParams({});
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <>
      <SEO
        title="בלוג"
        description="מאמרים, תובנות ומדריכים מעשיים למשווקים דיגיטליים שרוצים לצאת ממלכודת הריטיינר ולבנות נכסים דיגיטליים משלהם."
        path="/blog"
      />

      <div className="min-h-screen bg-[#FDF8FA]" dir="rtl">
        {/* Hero */}
        <section className="pt-28 pb-12 bg-gradient-to-b from-[#F8EEF2] to-[#FDF8FA]">
          <div className="max-w-5xl mx-auto px-5 text-center">
            <p className="text-[#C5366A] font-bold text-sm tracking-wider mb-3" data-testid="blog-kicker">הבלוג של המרכז לעצמאות דיגיטלית</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#2D2D2D] mb-4" data-testid="blog-heading">
              תובנות, אסטרטגיות ומדריכים מעשיים
            </h1>
            <p className="text-[#6B6B6B] text-base md:text-lg max-w-2xl mx-auto">
              כל מה שאתה צריך לדעת כדי לצאת ממלכודת הריטיינר ולהתחיל לבנות נכסים דיגיטליים שמייצרים הכנסה פסיבית.
            </p>
          </div>
        </section>

        {/* Categories */}
        <div className="max-w-5xl mx-auto px-5 mb-10 -mt-2">
          <div className="flex flex-wrap gap-2 justify-center" data-testid="category-filter">
            {Object.entries(CATEGORY_MAP).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleCategoryClick(key)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === key
                    ? 'bg-[#C5366A] text-white shadow-md'
                    : 'bg-white text-[#6B6B6B] border border-[#E8D5DD] hover:border-[#C5366A] hover:text-[#C5366A]'
                }`}
                data-testid={`category-${key || 'all'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Posts Grid */}
        <div className="max-w-5xl mx-auto px-5 pb-20">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-3 border-[#C5366A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-[#6B6B6B] py-16 text-lg">אין מאמרים בקטגוריה זו עדיין.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="posts-grid">
              {posts.map((post) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.slug}
                  className="group bg-white rounded-xl border border-[#E8D5DD]/60 hover:border-[#C5366A]/40 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                  data-testid={`post-card-${post.slug}`}
                >
                  {/* Thumbnail */}
                  {post.thumbnail && (
                    <img src={post.thumbnail} alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-[1.03] transition-transform duration-300" loading="lazy" />
                  )}
                  {/* Category badge + reading time */}
                  <div className="p-5 pb-0 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#C5366A] bg-[#F8EEF2] px-2.5 py-1 rounded-full uppercase tracking-wide">
                      <Tag className="w-3 h-3" />
                      {post.category_label || CATEGORY_MAP[post.category] || post.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-[#999]">
                      <Clock className="w-3 h-3" />
                      {post.reading_time} דק' קריאה
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <h2 className="text-lg md:text-xl font-bold text-[#2D2D2D] mb-3 leading-snug group-hover:text-[#C5366A] transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-[#6B6B6B] text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#999] text-xs">{formatDate(post.published_at)}</span>
                      <span className="flex items-center gap-1 text-[#C5366A] text-sm font-medium group-hover:gap-2 transition-all">
                        קרא עוד <ArrowLeft className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogPage;
